import * as queryString from 'querystring';
import { generatePath } from 'react-router-dom';
import type { IAttachmentRead, IMessage, IMessageParams, INewMessage, ISecureInboxSettings, ISecureInboxTopic, IUserMailbox } from '../../domain';
import { ApiBaseService } from '../api-base-service';

const ATTACHMENT_ID_PLACEHOLDER = ':attachmentId';
const MAILBOX_ID_PLACEHOLDER = ':mailboxId';
const MESSAGE_ID_PLACEHOLDER = ':messageId';
const ENDPOINTS = {
    ATTACHMENTS: '/attachments',
    MESSAGES: '/messages',
    MAILBOXES: '/mailboxes',
    SETTINGS: '/settings',
    TOPICS: '/topics',
};

export const secureInboxApiUrls = {
    archiveMessage: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}/archive`,
    attachments: ENDPOINTS.ATTACHMENTS,
    attachment: `${ENDPOINTS.ATTACHMENTS}/${ATTACHMENT_ID_PLACEHOLDER}`,
    attachmentUpload: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}/attachments`,
    attachmentContent: `${ENDPOINTS.ATTACHMENTS}/${ATTACHMENT_ID_PLACEHOLDER}/content`,
    deleteMessage: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}`,
    fetchMailboxes: `${ENDPOINTS.MAILBOXES}/users`,
    fetchMessage: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}`,
    fetchMessages: `${ENDPOINTS.MESSAGES}`,
    fetchSettings: ENDPOINTS.SETTINGS,
    fetchTopics: ENDPOINTS.TOPICS,
    fetchUnreadCount: `${ENDPOINTS.MAILBOXES}/${MAILBOX_ID_PLACEHOLDER}/unread-count`,
    markAsRead: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}/read?sendReadConfirmation=true`,
    sendMessage: `${ENDPOINTS.MAILBOXES}/${MAILBOX_ID_PLACEHOLDER}/messages`,
    queueMessage: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}/queue`,
    unarchiveMessage: `${ENDPOINTS.MESSAGES}/${MESSAGE_ID_PLACEHOLDER}/unarchive`,
};

export class SecureInboxApiService extends ApiBaseService {
    constructor(context = '/delegate/secure-inbox/v4') {
        super(context);
    }

    async archiveMessage(messageId: string) {
        return this.http.post(generatePath(secureInboxApiUrls.archiveMessage, { messageId }));
    }

    async deleteAttachment(attachmentId: string) {
        return this.http.delete(generatePath(secureInboxApiUrls.attachment, { attachmentId }));
    }

    async deleteMessage(messageId: string) {
        return this.http.delete(generatePath(secureInboxApiUrls.deleteMessage, { messageId }));
    }

    async fetchAttachments(messageIds: string[]): Promise<IAttachmentRead[]> {
        const params = {
            'message-id': messageIds ? messageIds.join(',') : undefined,
        };
        const { data } = await this.http.get<IAttachmentRead[]>(secureInboxApiUrls.attachments, { params });

        return data;
    }

    async fetchAttachment(attachmentId: string): Promise<IAttachmentRead> {
        const { data } = await this.http.get<IAttachmentRead>(generatePath(secureInboxApiUrls.attachment, { attachmentId }));

        return data;
    }

    async fetchAttachmentContent(attachmentId: string): Promise<Blob> {
        const { data, headers } = await this.http.get<Blob>(generatePath(secureInboxApiUrls.attachmentContent, { attachmentId }), { responseType: 'blob' });

        return new Blob([data], { type: headers['content-type'] });
    }

    async fetchMailboxes(externalIds?: string[]): Promise<IUserMailbox[]> {
        const params = {
            'external-ids': externalIds ? externalIds.join(',') : undefined,
        };
        const { data } = await this.http.get<IUserMailbox[]>(secureInboxApiUrls.fetchMailboxes, { params });

        return data;
    }

    async fetchMessage(messageId: string): Promise<IMessage> {
        const { data } = await this.http.get<IMessage>(generatePath(secureInboxApiUrls.fetchMessage, { messageId }));

        return data;
    }

    async fetchMessages(messageParams: IMessageParams): Promise<{ messages: IMessage[]; totalCount: number }> {
        const { 'mailbox-id': mailboxIds = [], categories, page = 1, 'page-size': size = 50, sort, archived } = messageParams;
        const params: any = {
            categories,
            page,
            'page-size': size,
            sort,
            archived,
            'mailbox-id': mailboxIds.join(','),
        };

        const { data, headers } = await this.http.get<IMessage[]>(secureInboxApiUrls.fetchMessages, { params, paramsSerializer: queryString.stringify });

        return { messages: data, totalCount: headers['x-total-count'] };
    }

    async fetchSettings(): Promise<ISecureInboxSettings> {
        const { data } = await this.http.get<ISecureInboxSettings>(secureInboxApiUrls.fetchSettings);

        return data;
    }

    async fetchTopics(): Promise<ISecureInboxTopic[]> {
        const { data } = await this.http.get<ISecureInboxTopic[]>(secureInboxApiUrls.fetchTopics);

        return data;
    }

    async fetchUnreadCount(mailboxId: string): Promise<number> {
        const { data } = await this.http.get<number>(generatePath(secureInboxApiUrls.fetchUnreadCount, { mailboxId }));

        return data;
    }

    async markAsRead(messageId: string) {
        return this.http.post(generatePath(secureInboxApiUrls.markAsRead, { messageId }));
    }

    async sendMessage(mailboxId: string, message: INewMessage) {
        return this.http.post(generatePath(secureInboxApiUrls.sendMessage, { mailboxId }), message);
    }

    async queueMessage(messageId: string) {
        return this.http.post(generatePath(secureInboxApiUrls.queueMessage, { messageId }));
    }

    async unarchiveMessage(messageId: string) {
        return this.http.post(generatePath(secureInboxApiUrls.unarchiveMessage, { messageId }));
    }

    /** The privilege chs-secinb:messages-attachments:create has to be granted to use this endpoint. */
    async uploadAttachment(messageId: string, attachment: Blob, attachmentName: string) {
        const formData = new FormData();
        formData.append('file', attachment, attachmentName);
        formData.append('name', attachmentName);

        return this.http.post(generatePath(secureInboxApiUrls.attachmentUpload, { messageId }), formData);
    }
}
